import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { ArticleFooterBodyAd, ArticleFooterAd } from "@/components/ArticleAds";
import { StoryIllustration } from "@/components/StoryIllustration";
import { ShareButton } from "@/components/ShareButton";
import { StoryProse } from "@/components/StoryProse";
import { StoryGlossaryLinks } from "@/components/StoryGlossaryLinks";


import { getStory, relatedStories, STORIES } from "@/data/stories";
import { MailchimpForm } from "@/components/MailchimpForm";

const SITE = "https://mrbquotes.com";

export const Route = createFileRoute("/stories/$slug")({
  loader: ({ params }) => {
    const story = getStory(params.slug);
    if (!story) throw notFound();
    return { story };
  },
  head: ({ loaderData }) => {
    const s = loaderData?.story;
    if (!s) {
      return { meta: [{ title: "Story not found — Mr. B Quotes" }, { name: "robots", content: "noindex" }] };
    }
    const url = `${SITE}/stories/${s.slug}`;
    const title = `${s.title} — Mr. B Quotes Stories`;
    return {
      meta: [
        { title },
        { name: "description", content: s.dek },
        { property: "og:title", content: s.title },
        { property: "og:description", content: s.dek },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: s.title },
        { name: "twitter:description", content: s.dek },
        { name: "article:published_time", content: s.date },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: s.title,
            description: s.dek,
            datePublished: s.date,
            articleSection: s.theme,
            mainEntityOfPage: url,
            author: { "@type": "Organization", name: "Mr. B Quotes", url: SITE },
            publisher: { "@type": "Organization", name: "Mr. B Quotes", url: SITE, logo: { "@type": "ImageObject", url: `${SITE}/favicon.png` } },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE },
              { "@type": "ListItem", position: 2, name: "Stories", item: `${SITE}/stories` },
              { "@type": "ListItem", position: 3, name: s.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="min-h-screen">
      <SiteHeader sub="Quotes · Stories" active="stories" />
      <main className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="font-display text-5xl text-gold">Story not found</h1>
        <p className="mt-4 text-muted-foreground">This page hasn't been written yet.</p>
        <Link to="/stories" className="mt-8 inline-block rounded-full border border-border px-5 py-2 text-sm hover:border-gold">All {STORIES.length} stories →</Link>
      </main>
      <SiteFooter />
    </div>
  ),
  component: StoryPage,
});

function StoryPage() {
  const { story: s } = Route.useLoaderData();
  const related = relatedStories(s.slug, 3);
  const shareText = `"${s.title}" — ${s.dek}\n\nRead at mrbquotes.com/stories/${s.slug}`;

  return (
    <div className="min-h-screen">
      <SiteHeader sub="Quotes · Stories" active="stories" />

      <div className="border-b border-border/60">
        <div className="mx-auto max-w-6xl px-4 pt-6">
          <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
            <Link to="/" className="hover:text-gold">Home</Link>
            <span className="mx-2 opacity-50">/</span>
            <Link to="/stories" className="hover:text-gold">Stories</Link>
            <span className="mx-2 opacity-50">/</span>
            <span className="text-foreground">{s.title}</span>
          </nav>
        </div>
        <div className="mx-auto max-w-6xl px-4 py-6">
          <div className="overflow-hidden rounded-2xl border border-border bg-card aspect-[5/2] sm:aspect-[16/6]">
            <StoryIllustration slug={s.slug} title={s.title} theme={s.theme} variant="banner" className="h-full w-full" />
          </div>
        </div>
      </div>

      <article className="mx-auto max-w-3xl px-4 py-12 sm:py-16">
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em]">
          <span className="text-gold">{s.theme}</span>
        </div>
        <h1 className="mt-2 font-display text-4xl sm:text-6xl leading-tight">{s.title}</h1>
        <p className="mt-5 text-xl text-muted-foreground leading-relaxed border-l-2 border-gold pl-5 italic">{s.dek}</p>

        <div className="prose prose-invert mt-10 max-w-none space-y-5 text-base sm:text-lg text-foreground/90 leading-relaxed">
          <StoryProse paragraphs={s.body} />
        </div>


        <figure className="mt-12 rounded-2xl border border-gold/30 bg-gold/5 p-6 sm:p-8 text-center">
          <div className="text-[10px] uppercase tracking-widest text-gold">A line to carry</div>
          <blockquote className="mt-3 font-display text-2xl sm:text-3xl leading-snug">&ldquo;{s.pullQuote.text}&rdquo;</blockquote>
          <figcaption className="mt-4 text-sm text-muted-foreground">— {s.pullQuote.author}</figcaption>
        </figure>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <ShareButton text={shareText} title={`${s.title} — Mr. B Quotes`} label="Share this story" />
          <Link to="/stories" className="rounded-full border border-border px-5 py-2.5 text-sm hover:border-gold transition">← All stories</Link>
        </div>

        <StoryGlossaryLinks theme={s.theme} slug={s.slug} />

        <ArticleFooterBodyAd />




        {related.length > 0 && (
          <section className="mt-14 border-t border-border/60 pt-10">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Keep reading</div>
            <h2 className="mt-2 font-display text-2xl">More stories</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to="/stories/$slug"
                  params={{ slug: r.slug }}
                  className="group rounded-xl border border-border bg-card/40 overflow-hidden transition hover:border-gold"
                >
                  <div className="aspect-[5/3]">
                    <StoryIllustration slug={r.slug} title={r.title} theme={r.theme} variant="card" className="h-full w-full" />
                  </div>
                  <div className="p-4">
                    <div className="text-[10px] uppercase tracking-widest text-gold">{r.theme}</div>
                    <div className="mt-1 font-display text-lg text-foreground group-hover:text-gold transition">{r.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>

      <ArticleFooterAd />

      <aside className="border-t border-border/60 bg-ink/40">
        <div className="mx-auto max-w-2xl px-4 py-14 sm:py-16">
          <MailchimpForm />
        </div>
      </aside>


      <SiteFooter />
    </div>
  );
}
